import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const ACCREDITATION_URL = "https://gcsb.uk/accreditation";
const TRUSTMARKS_URL = "https://gcsb.uk/trustmarks";
const T = (n) => `/trustmarks/${n}.webp`;

const ORG = [
  { img: "partner", title: "GCSB Accredited Partner", scope: "CSCE, CSP, CSL and CCSO assessment delivery · Accredited since 2026", id: "GCSB-AP-2026-004" },
  { img: "coe", title: "GCSB Centre of Excellence", scope: "Exceptional achievement in education, innovation and professional influence", id: "GCSB-AP-2026-002" },
  { img: "partner", title: "GCSB Training Partner (per qualification) - CSCE", scope: "Converged Security Cyber Expert", id: "GCSB-AP-2026-005" },
  { img: "partner", title: "GCSB Training Partner (per qualification) - CSP", scope: "Converged Security Professional", id: "GCSB-AP-2026-006" },
  { img: "partner", title: "GCSB Training Partner (per qualification) - CSL", scope: "Converged Security Leadership", id: "GCSB-AP-2026-007" },
  { img: "partner", title: "GCSB Training Partner (per qualification) - CCSO", scope: "Chief Converged Security Officer", id: "GCSB-AP-2026-008" },
  { img: "partner", title: "GCSB Training Partner (per qualification) - PGDCSL", scope: "Postgraduate Executive Diploma in Converged Security Leadership", id: "GCSB-AP-2026-009" },
];

const FACULTY = [
  { img: "fellow", title: "GCSB Fellow (FGCSB)", id: "GCSB-AP-2026-011", date: "06/10/2026", url: "https://credsverse.com/credentials/26a9c9a4-47aa-4351-bf06-ecaaa221a9eb" },
  { img: "distinguished", title: "Distinguished Fellow Academic (DFGCSB)", id: "GCSB-AP-2026-010", date: "07/10/2026", url: "https://credsverse.com/credentials/aff2ad2e-c673-4f3a-82c1-b854fb8decd8" },
  { img: "instructor", title: "GCSB Accredited Instructor (GCSB-AI)", id: "GCSB-AP-2026-012", date: "07/10/2026", url: "https://credsverse.com/credentials/2e330d61-9fd5-4428-92b5-da7e60c35973" },
  { img: "consultant", title: "GCSB Accredited Consultant (GCSB-AC)", id: "GCSB-AP-2026-014", date: "07/10/2026", url: "https://credsverse.com/credentials/e327f55f-5c8b-45ba-8096-1699f4b3b577" },
  { img: "auditor", title: "GCSB Accredited Auditor (GCSB-AA)", id: "GCSB-AP-2026-013", date: "07/10/2026", url: "https://credsverse.com/credentials/230e94a3-58b2-41b5-9a59-995812553be9" },
];

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { timeZone: "UTC" });

const Status = ({ date, status = "Active", country = "Spain" }) => (
  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
    <span className={`rounded-full font-bold uppercase tracking-wider px-2 py-0.5 ${status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>{status}</span>
    <span className="text-zinc-500">Awarded {date} · {country}</span>
  </div>
);

const Card = ({ img, title, scope, id, date = "05/10/2026", status, country, url, testid }) => {
  const body = (
    <article className="card-warm p-5 h-full flex items-start gap-4 hover:border-[var(--ink)] transition-colors" data-testid={testid}>
      <img src={T(img)} alt={title} loading="lazy" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full shrink-0 object-cover"/>
      <div className="min-w-0">
        <h3 className="font-display text-lg leading-tight">{title}</h3>
        {scope && <p className="text-xs text-zinc-600 mt-1">{scope}</p>}
        <Status date={date} status={status} country={country}/>
        <p className="mt-2 font-mono text-[11px] text-zinc-500 break-all">Certificate No.: <a href="https://gcsb.uk/verify" target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--terracotta)]" title="Verify at gcsb.uk/verify" data-testid={`verify-id-${id}`}>{id}</a></p>
        {url && <p className="mt-1 text-xs font-semibold text-[var(--terracotta)] inline-flex items-center gap-1">View online certificate <ExternalLink size={11}/></p>}
      </div>
    </article>
  );
  return url ? <a href={url} target="_blank" rel="noopener noreferrer" className="block h-full">{body}</a> : body;
};

export default function AccreditationPage() {
  useSeo({
    title: "Accreditation & Trustmarks",
    description: "Converged Security Institute is a GCSB Accredited Partner, Centre of Excellence and Training Partner for CSCE, CSP, CSL, CCSO and PGDCSL.",
    path: "/accreditation",
  });
  const [live, setLive] = useState(null);
  useEffect(() => { api.get("/accreditations").then((r) => setLive(r.data)).catch(() => {}); }, []);
  const toCard = (x) => ({ ...x, date: x.awarded_at ? fmtDate(x.awarded_at) : undefined,
    scope: x.slug === "accredited-partner" ? ORG[0].scope : undefined });
  const org = live?.org?.length ? live.org.map(toCard) : ORG;
  const faculty = live?.faculty?.length ? live.faculty.map(toCard) : FACULTY;
  return (
    <div data-testid="accreditation-page">
      <section className="bg-[var(--navy-deep)] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 grid md:grid-cols-[auto,1fr] gap-8 items-center">
          <img src={T("gcsb-seal")} alt="GCSB Credentialing Authority seal" className="w-32 h-32 sm:w-40 sm:h-40 rounded-full mx-auto md:mx-0"/>
          <div>
            <div className="eyebrow !text-[var(--gold-soft)]">Accreditation &amp; Trustmarks</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight mt-2">Independently accredited by GCSB</h1>
            <p className="text-zinc-300 mt-4 max-w-3xl text-sm sm:text-base">
              The Converged Security Institute (CSI) is the professional development and qualification body. The Global Converged
              Security Board (GCSB) is the independent standards, credentialing, accreditation and recognition authority. Every
              trustmark below is verifiable in the GCSB Trustmark Registry.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={ACCREDITATION_URL} target="_blank" rel="noopener noreferrer" className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="gcsb-accreditation-link">
                <ShieldCheck size={15}/> GCSB accreditation <ExternalLink size={13}/>
              </a>
              <a href={TRUSTMARKS_URL} target="_blank" rel="noopener noreferrer" className="chip px-5 py-3 bg-white/5 border-white/20 text-white hover:bg-white/10 inline-flex items-center gap-2" data-testid="gcsb-trustmarks-link">
                Trustmark Registry <ExternalLink size={13}/>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="eyebrow mb-1">Organisational trustmarks</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-2">CSI accreditations</h2>
        <p className="text-xs text-zinc-500 mb-6" data-testid="accreditation-sync-status">
          {live?.synced_at ? `Synced live from the GCSB Trustmark Registry · ${new Date(live.synced_at).toLocaleString("en-GB")}` : "Source: GCSB Trustmark Registry"}
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {org.map((o) => <Card key={o.id} {...o} testid={`trustmark-${o.id}`}/>)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="eyebrow mb-1">Faculty trustmark awards</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-2">Prof. Dr. Vladimir Bunic</h2>
        <p className="text-sm text-zinc-600 mb-6 max-w-3xl">GCSB Trustmark Awards recognise individuals for achievement beyond qualification attainment and membership - held by CSI's Chief Instructor.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {faculty.map((f) => <Card key={f.id} {...f} testid={`trustmark-${f.id}`}/>)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="card-warm p-6 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm sm:text-base text-zinc-700 max-w-2xl">Accreditation is awarded against published GCSB criteria, overseen by independent boards and external examiners.</p>
          <a href={ACCREDITATION_URL} target="_blank" rel="noopener noreferrer" className="btn-ink px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="gcsb-accreditation-link-bottom">
            Read about GCSB accreditation <ExternalLink size={13}/>
          </a>
        </div>
      </section>
    </div>
  );
}
